import LoadingBar from "@components/ui/LoadingBar";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Icon1 from "@assets/icons/Icon";
import { Text1, Text2 } from "@components/ui/texts/Texts";
import { useEffect, useState } from "react";

import DataGrid from "@components/ui/tables/DataGrid";
import { transformDossiersToTable } from "@components/ui/tables/transformToTables";
import { useDataStore } from "@store/user_org";
import { useDossierStore } from "@store/dossierStore";
import { isGUEST } from "@helpers/accessHelpers";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { searchDossiersFull } from "@services/algolia";

const typeTabs = [
  { value: "all", label: "Tous" },
  { value: "particulier", label: "Particuliers" },
  { value: "professionnel", label: "Professionnels" },
];

export default function DossiersScreen() {
  const orgData = useDataStore((state) => state.orgData);
  const userData = useDataStore((state) => state.userData);
  const isGuest = isGUEST(userData);
  const {
    dossiers,
    isLoading,
    isRefreshing,
    hasMore,
    hasPrev,
    fetchDossiers,
    fetchNextPage,
    fetchPrevPage,
    setSort,
    removeDossiers,
  } = useDossierStore();

  const [table, setTable] = useState<any>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("all");

  useEffect(() => {
    if (orgData?.id) fetchDossiers(orgData.id, activeTab);
  }, [orgData?.id, activeTab]);

  useEffect(() => {
    const table = transformDossiersToTable(dossiers, "all");
    setTable(table);
  }, [dossiers]);

  const handleDeleteSelected = () => {
    removeDossiers(selectedIds);
    showNotification({ type: "success", message: `${selectedIds.length} dossier(s) supprimé(s)` });
    setSelectedIds([]);
    setShowDeleteConfirm(false);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {showDeleteConfirm && (
        <ConfirmationModal
          title={`Supprimer ${selectedIds.length} dossier(s) ?`}
          message="Cette action est irréversible. Les dossiers et toutes leurs données seront définitivement supprimés."
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteSelected}
          confirmText="CONFIRMER"
        />
      )}

      {/* Header */}
      <div className="flex-shrink-0 pt-10 px-4 lg:px-10 pb-4">
        <div className="flex flex-col md:flex-row w-full max-w-1p2k justify-between">
          <div className="flex flex-col w-full md:w-400">
            <Text1>Dossiers</Text1>
            <Text2>Retrouvez tous vos dossiers ici.</Text2>
          </div>
          <div className="flex flex-row gap-2 items-center">
            {selectedIds.length > 0 && (
              <SecondaryButton
                startIcon={<Icon1 name="delete" size={18} />}
                onClick={() => setShowDeleteConfirm(true)}
              >
                {`Supprimer la sélection (${selectedIds.length})`}
              </SecondaryButton>
            )}
            {!isGuest && (
              <Button label="Nouveau dossier" onClick={() => {}} />
            )}
          </div>
        </div>
      </div>

      {/* DataGrid area */}
      <div className="flex-1 min-h-0 px-4 lg:px-10 pb-6 flex flex-col gap-3 w-full">
        {(isLoading || isRefreshing) && <LoadingBar />}
        <div className="flex-1 min-h-0 max-w-1p2k w-full">
          {table && (
            <DataGrid
              selectable={true}
              table={table}
              hide_lines={true}
              tabs={typeTabs}
              activeTab={activeTab}
              onTabChange={(tab) => setActiveTab(tab)}
              onSearch={async (query) => {
                if (!orgData?.id) return [];
                return searchDossiersFull(query, orgData.id);
              }}
              onSearchSelect={(option) => navigateTo({ route: "/dossier/" + option.value })}
              onClick={(item) => navigateTo({ route: "/dossier/" + item.id })}
              handleSelectionModelChange={(ids) => setSelectedIds(ids)}
              hasNext={hasMore}
              hasPrev={hasPrev}
              onNext={() => orgData?.id && fetchNextPage(orgData.id)}
              onPrev={() => orgData?.id && fetchPrevPage(orgData.id)}
              handleSortChange={(model) => {
                if (orgData?.id && model.length > 0)
                  setSort(orgData.id, model[0].field, model[0].sort);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}
